import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonAddress } from '@tonconnect/ui-react';

import { useWithdrawRequestMutation } from '@/api/queries/payment';
import { H1, H3, StringSmall } from '@/components/Typography/Typography';
import { BlueButton, SecondaryButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import css from './style.module.scss';

import toncoinPng from '/img/icons/toncoin-large.png';
import toncoinWebp from '/img/icons/toncoin-large.webp';
import usdPng from '/img/icons/usd-large.png';
import usdWebp from '/img/icons/usd-large.webp';

const WithDrawTransaction: React.FC = () => {
  const { t } = useTranslation();
  const usdtBalance = useAppSelector((state) => state.user.user?.balance?.usdt);
  const tonsPerUsdt = useAppSelector(
    (state) => state.user.extendedInfo?.exchangeRate?.tonsPerUsd,
  );
  const [withdrawRequest, { isLoading }] = useWithdrawRequestMutation();
  const { closeModal } = useContext(ModalContext);
  const address = useTonAddress();

  if (!usdtBalance || !tonsPerUsdt) return null;

  const withdrawFunds = (): void => {
    withdrawRequest({
      currency: 'ton',
      amount: usdtBalance || 0,
      wallet: address,
    })
      .unwrap()
      .then(() => closeModal())
      .catch((error) => {
        const message =
          'message' in error
            ? error.message
            : 'Something went wrong, please try again';
        showToast(message);
      });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-2">
        <H1 className="!text-[18px] !font-[700]">{t('Confirm withdraw')}</H1>
        <StringSmall className="!text-white/40 text-center !leading-[120%]">
          {t(
            'Funds are withdrawn in TON. The amount depends on the current exchange rate and transaction fee.',
          )}
        </StringSmall>
      </div>
      <StringSmall className="text-center">
        1 USD = {tonsPerUsdt?.toFixed(3)} TON
      </StringSmall>
      <div className="flex items-center gap-2 justify-center">
        <div className="flex flex-col items-center gap-[2px] w-[120px]">
          <Picture
            className="block w-12 h-12 shrink-0"
            img={usdPng}
            webp={usdWebp}
            alt="toncoin"
          />
          <H1 className="mt-2 text-center !text-[20px] !font-[700]">
            {usdtBalance} USD
          </H1>
          <StringSmall className="!text-white/40 block text-center">
            {t('Balance Withdraw')}
          </StringSmall>
        </div>
        <div className={css.arrows}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className={css.chevron}></div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-[2px] w-[120px]">
          <Picture
            className="block w-12 h-12 shrink-0"
            img={toncoinPng}
            webp={toncoinWebp}
            alt="toncoin"
          />
          <H1 className="mt-2 text-center !text-[20px] !font-[700]">
            {(usdtBalance * tonsPerUsdt).toFixed(2)} TON
          </H1>
          <StringSmall className="!text-white/40 block text-center">
            {t('You Get')}
          </StringSmall>
        </div>
      </div>
      <div className="w-full flex gap-2">
        <SecondaryButton
          onClick={closeModal}
          className="flex-1 !w-auto"
          text={t('Cancel')}
        />
        <BlueButton
          onClick={withdrawFunds}
          isLoading={isLoading}
          className="flex-1 !w-auto"
        >
          <H3 weight="extrabold" className="btn-text">
            {t('Confirm')}
          </H3>
        </BlueButton>
      </div>
    </>
  );
};

export default WithDrawTransaction;
